import { Injectable, inject, signal } from '@angular/core';
import { Location } from '@angular/common';
import { Router } from '@angular/router';
import { downloadBlob } from '@comparit/core';
import type { MarkedItem } from '@comparit/shared';
import type { Observable } from 'rxjs';
import { tap } from 'rxjs';
import type { ErgebnisInput } from '../interfaces/ergebnis-input.model';
import type { VergleichResponseMap } from '../interfaces/vergleich-input.model';
import type { VorgangDocument } from '@comparit/common-api';
import type { VorgangInput } from '../interfaces/vorgang-input.model';

/**
 * The Sparte seam behind the shared Tarifdetails page: the app subclass names its generated
 * Warenkorb and Vergleich services and its `Sparte`, this lib names no generated type. Same shape
 * as `VergleichBaseService` — transport as arrow properties, route literals beside them, because
 * routes are Sparte identity.
 */
@Injectable()
export abstract class TarifdetailsBaseService {
  /** Protected, not private: `startNewJourney()` is the app's to implement and has to navigate. */
  protected readonly router = inject(Router);
  private readonly location = inject(Location);

  /** The app's generated `createVorgang`; only the new id is read. */
  abstract readonly createVorgang$: (tarif: ErgebnisInput) => Observable<{ vorgangId: string }>;

  /** The app's generated `getVorgang` — enough to render the page right away. */
  abstract readonly loadVorgang$: (vorgangId: string) => Observable<VorgangInput>;

  /**
   * The app's generated `executeVorgangGetOrder`. The backend holds it until the insurer answered,
   * so one call per page load is enough — no polling; a reload is the retry.
   */
  abstract readonly executeGetOrder$: (vorgangId: string) => Observable<VorgangInput>;

  /** The app's generated `getVergleich` for this one tariff; the page reads its attribute groups. */
  abstract readonly fetchVergleich$: (identifier: MarkedItem) => Observable<VergleichResponseMap>;

  /**
   * How the Sparte finds a tariff, for the provisional price and the Laden page's lookup.
   * A seam member rather than a call into `ResultsBaseService.findTarif$`, because a Sparte can
   * have more than one source: kfz looks in its Angebot (betterOffer) data first, through a
   * `TarifLookupService` that itself injects `ResultsService` — overriding `findTarif$` there
   * would recurse. Sparten with only a calculation run assign `results.findTarif$` straight across.
   */
  abstract readonly findTarif$: (identifier: MarkedItem) => Observable<ErgebnisInput | undefined>;

  /**
   * The app's generated document call. Protected: callers go through `downloadDokument$`, which
   * saves the blob. The endpoint differs per Sparte — kfz has `getDocumentContent({ id })` on its
   * Sparte-specific client, the Sach apps `getVorgangDokumentInhalt({ sparte, id })` on the generic one.
   */
  protected abstract readonly fetchDokument$: (dokumentId: string) => Observable<object>;

  /** Where `ui-tarifdetails-laden` sends the customer once the Vorgang exists. */
  abstract readonly detailsUrl: (vorgangId: string) => string;

  /** Where Back goes when there is no history to return to — the Sparte's results page. */
  abstract readonly backUrl: string;

  /**
   * Set by `ui-tarifdetails-laden`: this page was reached from inside the app, so history holds
   * the page the customer actually came from. A plain flag on this root service rather than
   * router navigation state, which may not carry data.
   */
  readonly openedInApp = signal(false);

  /**
   * "Zurück zum Tarifvergleich". Reached from inside the app it returns where the customer came
   * from — the results page or the comparison, whichever it was. A reload or a deep link has no
   * such history, and falls back to `backUrl`.
   */
  goBack(): void {
    if (this.openedInApp()) {
      this.location.back();
      return;
    }
    void this.router.navigateByUrl(this.backUrl);
  }

  /**
   * Target of the "Zum Antrag" button. Unset until the Sparte has an Antrag, and the button is then
   * a no-op rather than a navigation to `[undefined]` — same trick as `VergleichBaseService.antragUrl`.
   */
  readonly antragUrl?: string;

  /**
   * Starting over after a sent Antrag: empty inputs, first step. What a Sparte has to clear is its
   * own business, so the app owns the whole move rather than handing the lib a url.
   */
  abstract startNewJourney(): void;

  /** The app supplies the call; the lib owns saving the blob under the document's own name. */
  downloadDokument$(doc: VorgangDocument): Observable<object> {
    return this.fetchDokument$(doc.id).pipe(tap(blob => downloadBlob(blob as Blob, doc.name)));
  }
}
